import { toast } from '@itsrever/design-system'
import { createRestocks } from '@/api/ops-api'
import { OpspbRestockItem } from '@itsrever/rever-api'
import { promiseTimer } from '@/utils'
import { useTranslation } from 'react-i18next'
import posthog from 'posthog-js'
import { RestockItem } from '@/domain/restock'
import { useProcessAction } from './use-process-action'

interface RestockRequest {
  processId: string
  ecommerceId: string
  items: RestockItem[]
}

export function useRestock() {
  const { t } = useTranslation()
  return useProcessAction({
    action: ({ processId, ecommerceId, items }: RestockRequest) =>
      Promise.all([
        createRestocks({
          opspbRestockItemsRequest: {
            process_id: processId,
            ecommerce_id: ecommerceId,
            restock_items: toApiRestock(items)
          }
        }),
        promiseTimer(5)
      ]),
    errorText: 'Error submitting restocks',
    reloadAfterMs: 2000,
    onSuccess: (_, { items }) => {
      posthog.capture('Independent Restock', {
        restockQuantity: items.reduce((acc, item) => acc + item.quantity, 0),
        defaultLocationsQuantity: items
          .filter((r) => r.location?.isDefault)
          .reduce((acc, item) => acc + item.quantity, 0),
        hasLocation: items.some((r) => r.location?.id)
      })
      toast({
        text: t('process_details_page.success_restock'),
        variant: 'success'
      })
    }
  })
}

function toApiRestock(items: RestockItem[]): OpspbRestockItem[] {
  return items.reduce((merged: OpspbRestockItem[], item) => {
    if (item.status !== 'SHOULD_RESTOCK') {
      return merged
    }

    const existingItem = merged.find((i) => i.line_item_id === item.lineItemId)

    if (existingItem && existingItem.quantity) {
      existingItem.quantity += item.quantity
    } else {
      merged.push({
        line_item_id: item.lineItemId,
        location_id: item.location?.id,
        quantity: item.quantity
      })
    }

    return merged
  }, [])
}
